const DATABASE_NAME = "rezzil-camera-captures";
const STORE_NAME = "captures";
const DATABASE_VERSION = 1;

const openDatabase = () => new Promise((resolve, reject) => {
  if (!window.indexedDB) {
    reject(new Error("This browser cannot preserve camera uploads offline."));
    return;
  }
  const request = window.indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
  request.onupgradeneeded = () => {
    const database = request.result;
    if (!database.objectStoreNames.contains(STORE_NAME)) {
      const store = database.createObjectStore(STORE_NAME, { keyPath: "captureId" });
      store.createIndex("sessionId", "sessionId", { unique: false });
    }
  };
  request.onsuccess = () => resolve(request.result);
  request.onerror = () => reject(request.error || new Error("Could not open camera storage."));
});

const withStore = async (mode, operation) => {
  const database = await openDatabase();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(STORE_NAME, mode);
    const store = transaction.objectStore(STORE_NAME);
    const request = operation(store);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error("Camera storage operation failed."));
    transaction.oncomplete = () => database.close();
    transaction.onerror = () => {
      database.close();
      reject(transaction.error || new Error("Camera storage transaction failed."));
    };
  });
};

export const savePendingCapture = (capture) => withStore("readwrite", (store) => store.put(capture));

export const deletePendingCapture = (captureId) => (
  withStore("readwrite", (store) => store.delete(captureId))
);

export const listPendingCaptures = async (sessionId) => {
  const captures = await withStore("readonly", (store) => store.getAll());
  return captures
    .filter((capture) => capture.sessionId === sessionId)
    .sort((left, right) => left.createdAt - right.createdAt);
};

export const deletePendingCapturesForSession = async (sessionId) => {
  const captures = await listPendingCaptures(sessionId);
  await Promise.all(captures.map((capture) => deletePendingCapture(capture.captureId)));
};

export const pruneExpiredCaptures = async (maxAgeMs) => {
  const cutoff = Date.now() - maxAgeMs;
  const captures = await withStore("readonly", (store) => store.getAll());
  const expired = captures.filter((capture) => capture.createdAt < cutoff);
  await Promise.all(expired.map((capture) => deletePendingCapture(capture.captureId)));
  return expired.length;
};
